export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Engine error details: `reasons` on approval_blocked, `gates` on gate_failed… */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE_URL =
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? "http://localhost:3339" : "");
/** For the few calls that cannot go through `api()`, such as an upload with progress. */
export const apiBaseUrl = BASE_URL;
const TOKEN_KEY = "prepai.worker.token";
const USER_ID_KEY = "prepai.worker.userId";

/**
 * The worker session, in `sessionStorage`.
 *
 * A worker token can rewrite the question bank, and the CMS runs on shared
 * office machines. `localStorage` kept it after the browser closed for as long
 * as the token lived; `sessionStorage` ends it with the tab. The server also
 * caps staff tokens at 12 hours. Anything left in `localStorage` by an older
 * build is wiped on first read.
 */
function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function purgeLegacy(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_ID_KEY);
  } catch {
    /* storage disabled */
  }
}

function read(key: string): string | null {
  try {
    return store()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) store()?.removeItem(key);
    else store()?.setItem(key, value);
  } catch {
    /* A session that survives only in memory is still a session. */
  }
}

purgeLegacy();

export const authToken = {
  get: (): string | null => read(TOKEN_KEY),
  set: (token: string): void => write(TOKEN_KEY, token),
  clear: (): void => write(TOKEN_KEY, null),
};

export const workerUserId = {
  get: (): string | null => {
    const stored = read(USER_ID_KEY);
    if (stored) return stored;

    // Fall back to the JWT's `sub` claim.
    const token = read(TOKEN_KEY);
    if (!token) return null;
    try {
      const sub = (JSON.parse(atob(token.split(".")[1]!)) as { sub?: string }).sub;
      if (sub) write(USER_ID_KEY, sub);
      return sub ?? null;
    } catch {
      return null;
    }
  },
  set: (id: string): void => write(USER_ID_KEY, id),
  clear: (): void => write(USER_ID_KEY, null),
};

/**
 * Called when the server says the session is over (expired, revoked, or the
 * worker was suspended). The app sends them to sign in rather than leaving
 * every screen failing with "Sign in to continue".
 */
let onSessionEnded: (() => void) | null = null;
export function setSessionEndedHandler(handler: (() => void) | null): void {
  onSessionEnded = handler;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  authenticated?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, authenticated = true } = options;
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  if (authenticated) {
    const token = authToken.get();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (
      payload as {
        error?: { code?: string; message?: string; details?: Record<string, unknown> };
      } | null
    )?.error;
    if (response.status === 401 && authenticated && authToken.get()) {
      authToken.clear();
      workerUserId.clear();
      onSessionEnded?.();
    }
    throw new ApiError(
      response.status,
      error?.code ?? "UNKNOWN",
      error?.message ?? "Something went wrong.",
      error?.details ?? {},
    );
  }

  return payload as T;
}
